import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Shield, Mail, Phone, Hash } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { updateMyFormAutoAssign, uploadAvatar } from '../../services/collaborators/collaboratorService';
import { Avatar } from '../ui/Avatar';
import { AvatarCropModal } from '../ui/AvatarCropModal';
import { cn } from '../../lib/cn';
import { ProfileSignatureSection } from './ProfileSignatureSection';
import { ProfilePublicSection } from './ProfilePublicSection';
import { ProfilePasswordSection } from './ProfilePasswordSection';
import { ProfileColorsSection } from './ProfileColorsSection';
import { ProfileTaskDisplaySection } from './ProfileTaskDisplaySection';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrateur',
  lawyer: 'Avocat',
  secretary: 'Assistant(e)',
};

export function ProfilePage() {
  const collaborator = useAuthStore((s) => s.collaborator);
  const setCollaborator = useAuthStore((s) => s.setCollaborator);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [saved, setSaved] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);

  const avatarMutation = useMutation({
    mutationFn: (blob: Blob) => {
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      return uploadAvatar(collaborator!.id, file);
    },
    onSuccess: (url) => {
      if (collaborator) setCollaborator({ ...collaborator, avatar_url: url } as typeof collaborator);
      queryClient.invalidateQueries({ queryKey: ['collaborators'] });
      setAvatarError(null);
    },
    onError: () => setAvatarError("Échec de l'upload. Réessayez."),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset input so the same file can be re-selected
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setAvatarError('Fichier trop volumineux (max 10 Mo).');
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setCropImageUrl(objectUrl);
  }

  function handleCropComplete(blob: Blob) {
    setCropImageUrl(null);
    avatarMutation.mutate(blob);
  }

  function handleCropCancel() {
    if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
    setCropImageUrl(null);
  }

  const mutation = useMutation({
    mutationFn: (accept: boolean) =>
      updateMyFormAutoAssign(collaborator!.id, accept),
    onSuccess: (_data, accept) => {
      // Mise à jour optimiste du store local
      if (collaborator) {
        setCollaborator({ ...collaborator, accept_form_auto_assign: accept } as typeof collaborator);
      }
      queryClient.invalidateQueries({ queryKey: ['collaborators'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (!collaborator) return null;

  const accept = (collaborator as any).accept_form_auto_assign !== false;

  return (
    <div className="main-container-gdm">
    <div className="mx-auto max-w-2xl space-y-8">
      {/* Avatar */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6">
        <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-4">
          Photo de profil
        </h2>
        <div className="flex items-center gap-6">
          <div className="relative group">
            <Avatar name={collaborator.full_name} src={collaborator.avatar_url} size="2xl" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarMutation.isPending}
              className={cn(
                'absolute inset-0 rounded-full flex items-center justify-center',
                'bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity',
                avatarMutation.isPending && 'opacity-100 cursor-not-allowed',
              )}
              aria-label="Modifier la photo"
            >
              {avatarMutation.isPending ? (
                <span className="text-white text-xs font-medium">…</span>
              ) : (
                <Camera className="w-6 h-6 text-white" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-neutral-900">{collaborator.full_name}</p>
            <p className="text-xs text-neutral-400">
              Cliquez sur la photo pour la modifier.
              <br />Formats acceptés : JPG, PNG, WebP — max 10 Mo.
            </p>
            {avatarError && (
              <p className="text-xs text-red-600">{avatarError}</p>
            )}
            {avatarMutation.isSuccess && (
              <p className="text-xs text-green-600 font-medium">Photo mise à jour.</p>
            )}
          </div>
        </div>
      </div>

      {cropImageUrl && (
        <AvatarCropModal
          imageUrl={cropImageUrl}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {/* Infos identité */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6 space-y-4">
        <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
          Identité
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InfoRow icon={<Shield className="w-4 h-4" />} label="Rôle">
            {ROLE_LABELS[collaborator.role] ?? collaborator.role}
          </InfoRow>
          <InfoRow icon={<Mail className="w-4 h-4" />} label="Email">
            {collaborator.email}
          </InfoRow>
          {collaborator.phone && (
            <InfoRow icon={<Phone className="w-4 h-4" />} label="Téléphone">
              {collaborator.phone}
            </InfoRow>
          )}
          {(collaborator as any).bar_number && (
            <InfoRow icon={<Hash className="w-4 h-4" />} label="Barreau">
              {(collaborator as any).bar_number}
            </InfoRow>
          )}
        </div>
      </div>

      {/* Signature */}
      <ProfileSignatureSection />

      {/* Profil public */}
      <ProfilePublicSection />

      {/* Couleurs de l'agenda */}
      <ProfileColorsSection />

      {/* Étiquettes des tâches */}
      <ProfileTaskDisplaySection />

      {/* Mot de passe */}
      <ProfilePasswordSection />

      {/* Préférences formulaire de contact */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6 space-y-4">
        <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
          Formulaire de contact
        </h2>

        <div className="flex items-start justify-between gap-6">
          <div className="space-y-1">
            <p className="text-sm font-medium text-neutral-900">
              Assignation automatique des tickets
            </p>
            <p className="text-sm text-neutral-500 max-w-md">
              Quand un formulaire de contact du site est configuré avec votre adresse email, le
              ticket est automatiquement assigné dans CIME.{' '}
              {accept
                ? "Désactivez cette option si vous préférez que les tickets passent par la file d’attente générale — vous recevrez quand même l’email de notification."
                : "Vous recevez toujours l’email de notification, mais les tickets ne vous sont plus assignés automatiquement."}
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={accept}
            onClick={() => mutation.mutate(!accept)}
            disabled={mutation.isPending}
            className={cn(
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent',
              'transition-colors duration-200 ease-in-out focus-visible:outline-none',
              'focus-visible:ring-2 focus-visible:ring-cime-primary focus-visible:ring-offset-2',
              accept ? 'bg-cime-primary' : 'bg-neutral-300',
              mutation.isPending && 'opacity-50 cursor-not-allowed',
            )}
          >
            <span
              className={cn(
                'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0',
                'transition duration-200 ease-in-out',
                accept ? 'translate-x-5' : 'translate-x-0',
              )}
            />
          </button>
        </div>

        {saved && (
          <p className="text-xs text-green-600 font-medium">Préférence enregistrée.</p>
        )}
        {mutation.isError && (
          <p className="text-xs text-red-600">
            Erreur lors de l'enregistrement. Réessayez.
          </p>
        )}
      </div>
    </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-neutral-400 mt-0.5">{icon}</span>
      <div>
        <p className="text-xs text-neutral-400 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-sm text-neutral-900">{children}</p>
      </div>
    </div>
  );
}
